/**
 * REQ-059 PASO 2 — controles de tarea de la barra global de jornada.
 *  · «Cambiar tarea»: solo tareas PROPIAS abiertas (el backend lo exige: 403 TASK_NOT_OWN); cambio atómico.
 *  · «Finalizar tarea» ≠ «Finalizar jornada»: parar la tarea no cierra la jornada.
 *  · «Finalizar jornada»: flujo existente (cierra de forma segura la ejecución propia activa).
 */
import { useCallback, useEffect, useState } from 'react';
import { taskHierarchyLabel } from '../tasks/taskTypes';
import { activityApi, errText, type LiveState } from '../../stores/activityApi';
import { workApi, type TaskRow } from '../../stores/workApi';
import FinishDayButton from './FinishDay';

export interface SwitchOption { taskId: string; projectId: string; label: string; current: boolean }

/** Opciones del selector: tareas abiertas (la API ya filtra a las propias); la actual se marca y no es elegible. */
export function switchOptions(tasks: TaskRow[], currentTaskId: string | null | undefined): SwitchOption[] {
  return tasks
    .filter((t) => !['DONE', 'CANCELLED'].includes(String(t.status)))
    .map((t) => ({ taskId: t.id, projectId: t.project.id, current: t.id === currentTaskId, label: taskHierarchyLabel({ projectName: t.project.name, processName: t.process?.name ?? null, taskCode: t.taskCode, title: t.title }) }));
}

/** Mensaje de error legible para el código TASK_NOT_OWN / NEXUS_STILL_RUNNING / ALREADY_ON_TASK. */
export function switchErrorText(e: unknown): string {
  const raw = errText(e, 'No se pudo cambiar de tarea');
  if (/TASK_NOT_OWN/.test(raw)) return 'Esa tarea no es tuya: solo puedes ejecutar tareas asignadas a ti.';
  if (/NEXUS_STILL_RUNNING/.test(raw)) return 'Nexus sigue trabajando en otra tarea; finalízalo antes de cambiar.';
  if (/ALREADY_ON_TASK/.test(raw)) return 'Ya estás trabajando en esa tarea.';
  return raw;
}

export default function WorkdayTaskBar({ live, onChanged }: { live: LiveState; onChanged: () => void | Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<SwitchOption[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const currentTaskId = live.current?.taskId ?? null;

  const loadOptions = useCallback(async () => {
    try { setOptions(switchOptions(await workApi.myTasks({ status: 'OPEN', scope: 'me' }), currentTaskId)); setErr(null); }
    catch (e) { setErr(errText(e, 'No se pudieron cargar tus tareas')); }
  }, [currentTaskId]);

  useEffect(() => { if (open) void loadOptions(); }, [open, loadOptions]);

  const doSwitch = async (o: SwitchOption) => {
    if (o.current) return;
    setBusy(true); setErr(null);
    try { await activityApi.switchTask(o.projectId, o.taskId); setOpen(false); await onChanged(); }
    catch (e) { setErr(switchErrorText(e)); }
    finally { setBusy(false); }
  };

  /** Finalizar tarea: detiene el cronómetro propio y marca la tarea como hecha; la jornada sigue abierta. */
  const endTask = async () => {
    setBusy(true); setErr(null);
    try {
      await workApi.stopTimer();
      if (currentTaskId) await workApi.updateTask(currentTaskId, { status: 'DONE' });
      setConfirmEnd(false); await onChanged();
    } catch (e) { setErr(errText(e, 'No se pudo finalizar la tarea')); }
    finally { setBusy(false); }
  };

  const btn = 'rounded-lg border border-outline-variant px-2 py-0.5 text-[11px] text-on-surface-variant hover:text-on-surface disabled:opacity-50';
  return (
    <div className="relative mt-0.5 flex flex-wrap items-center gap-1.5" data-testid="wd-taskbar">
      <button className={btn} disabled={busy} onClick={() => setOpen((v) => !v)} data-testid="wd-switch-open">Cambiar tarea</button>
      {currentTaskId && !confirmEnd && <button className={btn} disabled={busy} onClick={() => setConfirmEnd(true)} data-testid="wd-end-task">Finalizar tarea</button>}
      {currentTaskId && confirmEnd && (
        <span className="flex items-center gap-1 text-[11px]" data-testid="wd-end-task-confirm">
          ¿Marcar como hecha? La jornada sigue abierta.
          <button className={btn} disabled={busy} onClick={endTask} data-testid="wd-end-task-yes">Sí, finalizar tarea</button>
          <button className={btn} disabled={busy} onClick={() => setConfirmEnd(false)}>No</button>
        </span>
      )}
      <FinishDayButton className={btn} onChanged={onChanged} />
      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 max-h-72 w-80 overflow-auto rounded-lg border border-outline-variant bg-surface p-1 shadow-lg" data-testid="wd-switch-list">
          {options.length === 0 && <div className="p-2 text-[11px] text-outline" data-testid="wd-switch-empty">No tienes tareas abiertas asignadas.</div>}
          {options.map((o) => (
            <button key={o.taskId} disabled={busy || o.current} onClick={() => doSwitch(o)} data-testid="wd-switch-option"
              className="block w-full truncate rounded px-2 py-1 text-left text-[11px] text-on-surface hover:bg-surface-variant disabled:opacity-50">
              {o.current ? '● ' : ''}{o.label}
            </button>
          ))}
        </div>
      )}
      {err && <span className="text-[11px] text-[#F87171]" data-testid="wd-taskbar-error">{err}</span>}
    </div>
  );
}
